import { useCallback, useRef } from 'react';
import {
  cancelAnimation,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

const SCROLL_DOWN_THRESHOLD = 15;
const SCROLL_UP_THRESHOLD = 30;
const DIRECTION_CHANGE_MIN_DELTA = 3; // 방향 전환으로 인정하는 최소 이동량

export const useHomeScroll = () => {
  const headerVisible = useSharedValue(1);
  const isHidden = useRef(false);
  const lastDirectionChangeY = useRef(0);
  const lastScrollY = useRef(0);
  const lastDirection = useRef<'up' | 'down' | null>(null);

  const onScrollY = useCallback(
    (scrollY: number) => {
      // 최상단이면 항상 보이기
      if (scrollY <= 0) {
        if (isHidden.current) {
          cancelAnimation(headerVisible);
          headerVisible.value = withTiming(1, { duration: 200 });
          isHidden.current = false;
        }
        lastScrollY.current = 0;
        lastDirectionChangeY.current = 0;
        lastDirection.current = null;
        return;
      }

      const delta = scrollY - lastScrollY.current;

      // 미세한 흔들림 무시
      if (Math.abs(delta) < DIRECTION_CHANGE_MIN_DELTA) {
        lastScrollY.current = scrollY;
        return;
      }

      const currentDirection: 'up' | 'down' = delta > 0 ? 'down' : 'up';
      lastScrollY.current = scrollY;

      // 방향이 바뀌면 기준점 리셋
      if (
        lastDirection.current !== null &&
        currentDirection !== lastDirection.current
      ) {
        lastDirectionChangeY.current = scrollY;
      }
      lastDirection.current = currentDirection;

      const distance = Math.abs(scrollY - lastDirectionChangeY.current);

      const threshold =
        currentDirection === 'down'
          ? SCROLL_DOWN_THRESHOLD
          : SCROLL_UP_THRESHOLD;
      if (distance > threshold) {
        if (currentDirection === 'down' && !isHidden.current) {
          cancelAnimation(headerVisible);
          headerVisible.value = withTiming(0, { duration: 200 });
          isHidden.current = true;
        } else if (currentDirection === 'up' && isHidden.current) {
          cancelAnimation(headerVisible);
          headerVisible.value = withTiming(1, { duration: 200 });
          isHidden.current = false;
        }
      }
    },
    [headerVisible],
  );

  return { headerVisible, onScrollY };
};